<script setup>
import { computed } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { Users, HardHat, LogOut, MessageCircle } from 'lucide-vue-next'
import Logo from '../Components/Logo.vue'

const page = usePage()
const unit = computed(() => page.props.unit ?? {})
const staff = computed(() => page.props.staff ?? {})

const currentPath = computed(() => page.url.split('?')[0])
const isActive = (href) =>
  href === '/admin' ? currentPath.value === '/admin' : currentPath.value.startsWith(href)

const menu = [
  { label: 'Clientes', href: '/admin', icon: Users },
  { label: 'Mensagens', href: '/admin/mensagens', icon: MessageCircle, badge: 'awaiting' },
  { label: 'Equipe', href: '/admin/equipe', icon: HardHat },
]

// Conversas esperando resposta, para a equipe ver sem abrir a caixa.
const awaiting = computed(() => staff.value.awaiting ?? 0)
</script>

<template>
  <div class="min-h-screen bg-up-bg font-sans text-up-navy">
    <header class="bg-up-navy text-white">
      <div class="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-4 sm:px-6">
        <Link href="/admin" class="flex items-center gap-3">
          <Logo class="text-base" />
          <!--
            Faixa laranja e a palavra ADMINISTRAÇÃO: quem abre precisa saber
            num relance que não está no portal do cliente.
          -->
          <span class="rounded bg-up-orange px-2 py-0.5 text-[11px] font-semibold tracking-[0.14em] uppercase">
            Administração
          </span>
        </Link>

        <!--
          A unidade fica no cabeçalho porque cada uma é um banco separado: sem
          isto, dá para editar o cliente certo na unidade errada sem perceber.
        -->
        <span class="rounded border border-white/25 px-2.5 py-1 text-xs tracking-[0.12em] uppercase">
          {{ unit.city }}
        </span>

        <nav class="flex items-center gap-1" aria-label="Seções">
          <Link
            v-for="item in menu"
            :key="item.href"
            :href="item.href"
            class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors"
            :class="isActive(item.href) ? 'bg-white/15 text-white' : 'text-white/70 hover:text-white'"
            :aria-current="isActive(item.href) ? 'page' : undefined"
          >
            <component :is="item.icon" class="h-4 w-4" aria-hidden="true" />
            {{ item.label }}
            <span
              v-if="item.badge === 'awaiting' && awaiting"
              class="rounded-full bg-up-orange px-1.5 py-0.5 text-[11px] font-semibold text-white"
              :aria-label="`${awaiting} conversa(s) esperando resposta`"
            >{{ awaiting }}</span>
          </Link>
        </nav>

        <div class="ml-auto flex items-center gap-4 text-sm">
          <span class="hidden text-white/70 sm:inline">{{ staff.name }}</span>
          <Link
            href="/admin/sair"
            method="delete"
            as="button"
            type="button"
            class="flex items-center gap-2 text-white/70 transition-colors hover:text-white"
          >
            <LogOut class="h-4 w-4" aria-hidden="true" />
            Sair
          </Link>
        </div>
      </div>
    </header>

    <main class="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <slot />
    </main>

    <footer class="mx-auto max-w-6xl px-4 pb-8 text-xs text-slate-500 sm:px-6">
      Área administrativa · {{ unit.name }} · as alterações aqui aparecem no portal do cliente.
    </footer>
  </div>
</template>
